"use client";

import { useState } from "react";
import { Notice, PillGroup, TextField } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  ExampleWords,
  LookupState,
  MeaningCard,
  revealField,
  WordRows,
} from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  answeredParam,
  normalizeLookup,
  type PartOfSpeech,
} from "@/lib/tool-kit/vocabulary/datamuse";
import {
  groupSynonyms,
  type SynonymResult,
  synonymQueries,
} from "@/lib/tool-kit/vocabulary/synonyms";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";

/** English strings. A translated content file overrides these through `ui`. */
export const SYNONYM_FINDER_STRINGS = {
  wordLabel: "Word",
  wordPlaceholder: "significant",
  wordHint:
    "Results appear as you type. Click any result to look up its synonyms in turn.",
  tryLabel: "Try:",
  examples: ["significant", "show", "argue", "important", "use"],
  emptyPrompt:
    "Type a word to see its synonyms and words with a similar meaning, grouped by part of speech.",
  filterLabel: "Part of speech",
  filterAll: "All",
  filterOption: "{label} ({count})",
  posGroups: {
    n: "Nouns",
    v: "Verbs",
    adj: "Adjectives",
    adv: "Adverbs",
    u: "Other",
  } as Record<PartOfSpeech, string>,
  posNames: {
    n: "noun",
    v: "verb",
    adj: "adjective",
    adv: "adverb",
    u: "other",
  } as Record<PartOfSpeech, string>,
  heading: "Synonyms for “{word}”",
  wordCount: "one:{count} word|other:{count} words",
  meaningLine: "{pos}: {definition}",
  synonymBadge: "synonym",
  legend:
    "Words marked “synonym” share a WordNet sense with your word; the rest have a related meaning. Read the definition before you swap one in.",
  noResults:
    "No synonyms or similar words for “{word}”. Check the spelling, or try the base form — run rather than running.",
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service. Check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer. Try again in a moment.",
  retry: "Try again",
  copy: "Copy",
  copied: "Copied!",
  copyWord: "Copy “{word}”",
  credit: "Word data from the Datamuse API",
} as const;

type Strings = typeof SYNONYM_FINDER_STRINGS;
type Filter = "all" | PartOfSpeech;

const FIELD_ID = "syn-word";
const MAX_INPUT = 60;
const RE_LETTER = /\p{L}/u;

function Results({
  word,
  result,
  filter,
  onFilter,
  onSelect,
  stale,
  strings,
}: {
  word: string;
  result: SynonymResult;
  filter: Filter;
  onFilter: (filter: Filter) => void;
  onSelect: (word: string) => void;
  stale: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (result.total === 0) {
    return <Notice>{t(strings.noResults, { word })}</Notice>;
  }
  const active = result.groups.some((group) => group.pos === filter)
    ? filter
    : "all";
  const groups = result.groups.filter(
    (group) => active === "all" || group.pos === active
  );
  const options = [
    { value: "all" as Filter, label: strings.filterAll },
    ...result.groups.map((group) => ({
      value: group.pos as Filter,
      label: t(strings.filterOption, {
        label: strings.posGroups[group.pos],
        count: fmt(group.entries.length),
      }),
    })),
  ];
  return (
    <div className={stale ? "space-y-5 opacity-60" : "space-y-5"}>
      <div aria-live="polite" className="space-y-1">
        <h2 className={WIDGET_H2}>{t(strings.heading, { word })}</h2>
        <p className={HINT}>
          {tPlural(strings.wordCount, locale, result.total, {
            count: fmt(result.total),
          })}
        </p>
      </div>
      <MeaningCard
        echo={result.echo}
        meaningLine={strings.meaningLine}
        posNames={strings.posNames}
      />
      {result.groups.length > 1 ? (
        <PillGroup
          label={strings.filterLabel}
          name="syn-pos"
          onChange={onFilter}
          options={options}
          value={active}
        />
      ) : null}
      <p className={HINT}>{strings.legend}</p>
      {groups.map((group) => (
        <section className="space-y-3" key={group.pos}>
          <h3 className={WIDGET_H3}>{strings.posGroups[group.pos]}</h3>
          <WordRows
            copyStrings={strings}
            onSelect={onSelect}
            rows={group.entries.map((entry) => ({
              word: entry.word,
              badge: entry.synonym ? strings.synonymBadge : undefined,
              detail: entry.definition ?? undefined,
            }))}
          />
        </section>
      ))}
    </div>
  );
}

export default function SynonymFinderWidget() {
  const strings = useToolUi(SYNONYM_FINDER_STRINGS);
  const [input, setInput] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const normalized = normalizeLookup(input);
  const word = RE_LETTER.test(normalized) ? normalized : "";
  const lookup = useDatamuse(word ? synonymQueries(word) : []);
  const answeredWord = answeredParam(lookup.answered, "rel_syn");
  const [strict, related] = lookup.results;
  const result =
    word && strict && related
      ? groupSynonyms(answeredWord, strict, related)
      : null;

  function lookUp(next: string) {
    setInput(next);
    setFilter("all");
    revealField(FIELD_ID);
  }

  return (
    <>
      <TextField
        hint={strings.wordHint}
        id={FIELD_ID}
        label={strings.wordLabel}
        onChange={(value) => setInput(value.slice(0, MAX_INPUT))}
        placeholder={strings.wordPlaceholder}
        type="search"
        value={input}
      />
      {word ? (
        <LookupState
          failure={lookup.failure}
          onRetry={lookup.retry}
          status={lookup.status}
          strings={strings}
        />
      ) : (
        <div className="space-y-3">
          <Notice>{strings.emptyPrompt}</Notice>
          <ExampleWords
            label={strings.tryLabel}
            onSelect={lookUp}
            words={strings.examples}
          />
        </div>
      )}
      {result ? (
        <Results
          filter={filter}
          onFilter={setFilter}
          onSelect={lookUp}
          result={result}
          stale={lookup.status === "loading"}
          strings={strings}
          word={answeredWord}
        />
      ) : null}
      <DatamuseCredit label={strings.credit} />
    </>
  );
}
